import {JobNodes} from "@cm/job-nodes/job-nodes"
import {imageProcessingTask} from "@cm/job-nodes/image-processing"
import type {InputMapsInfo, MaterialDetails} from "#material-maps-exporter/core/types"
import {Utility} from "@cm/job-nodes/utility"
import type {Config} from "#material-maps-exporter/targets/pbr/types"
import type {ThumbnailOptions} from "@cm/job-nodes"
import {generateThumbnailsDataObjectTask} from "@cm/job-nodes"
import {imageProcessingGraphForPbrExport} from "#material-maps-exporter/targets/pbr/image-processing-graph"
import {useLinearSpace} from "#material-maps-exporter/targets/pbr/export-config"

/**
 * The job that produces an export: one image-processing task for the conversion, a colour-space update
 * per map, the info file, all zipped and connected to the material's output row.
 *
 * Takes a config as written, never one read back from a row, so it has no legacy shape to handle.
 */
export function jobGraphForPbrExportConfig(
    config: Config,
    mapsInfo: InputMapsInfo,
    materialDetails: Pick<MaterialDetails, "id" | "organization">,
    platformVersion: string,
    zipFilename: string | null = null,
) {
    const imgProcTask = JobNodes.task(imageProcessingTask, {
        input: JobNodes.value({graph: imageProcessingGraphForPbrExport(config, mapsInfo)}),
    })

    const content: (typeof Utility.Zip.JobNodes)["folder"] extends (content: infer A) => unknown ? A : never = {}
    for (const map of config.maps) {
        const imageColorSpace = useLinearSpace(config, map.mapType) ? "LINEAR" : "SRGB"
        const thumbnailsTask = JobNodes.task(generateThumbnailsDataObjectTask, {
            input: JobNodes.struct({
                dataObject: JobNodes.get(
                    Utility.DataObject.update(JobNodes.get(imgProcTask, map.mapType), {
                        imageColorSpace: imageColorSpace,
                    }),
                    "dataObject",
                ),
                // we don't really want to generate any thumbnails here, we just want to update the color space in the image which the thumbnail service does as well
                options: JobNodes.value({
                    generateTiles: false,
                    generateThumbnails: [] as unknown as ThumbnailOptions["generateThumbnails"],
                }),
            }),
        })
        content[map.filename] = Utility.Zip.JobNodes.file(JobNodes.get(thumbnailsTask, "dataObject"))
    }
    content[config.info.name] = Utility.Zip.JobNodes.file(Utility.Zip.JobNodes.text(config.info.content))

    const zipTask = JobNodes.task(Utility.Zip.task, {
        input: JobNodes.struct({
            filename: JobNodes.value(zipFilename ?? config.name),
            destination: JobNodes.value({
                type: "taskOutputOnly",
                customerId: materialDetails.organization.legacyId,
            } as Utility.Zip.Destination),
            root: Utility.Zip.JobNodes.folder(content),
        }),
    })

    const updateMaterialOutputTask = JobNodes.task(Utility.MaterialOutput.task, {
        input: JobNodes.struct({
            operation: JobNodes.value("update" as const),
            fields: JobNodes.struct({
                materialId: JobNodes.value(materialDetails.id),
                dataObject: JobNodes.get(zipTask, "zipFile"),
            }),
        }),
    })

    return JobNodes.jobGraph(JobNodes.list([updateMaterialOutputTask, zipTask]), {platformVersion})
}
